import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { getCore } from '@dynamic-labs-sdk/client/core';

import type { PhantomEvents } from '../../phantomRedirect.broadcast.types';

/**
 * Registers listeners for Phantom redirect events broadcast from other tabs
 * (e.g. the redirect-completion tab), using the crossTabBroadcast service.
 *
 * Returns a cleanup function that unregisters all listeners — call it when the
 * operation is no longer pending (e.g. in a promise `.finally()`).
 *
 * @param listeners - Map of event names to handler functions
 * @param client - The Dynamic client instance
 * @returns Cleanup function that removes all registered listeners
 */
export const listenForPhantomBroadcast = (
  listeners: Partial<PhantomEvents>,
  client: DynamicClient
): (() => void) => {
  const { crossTabBroadcast } = getCore(client);

  const wrappedListeners = Object.entries(listeners).map(
    ([event, listener]) => {
      const wrapped = (args?: unknown) => {
        (listener as (args: unknown) => void)(
          args as Parameters<PhantomEvents[keyof PhantomEvents]>[0]
        );
      };

      crossTabBroadcast.on(event, wrapped);

      return { event, wrapped };
    }
  );

  return () => {
    wrappedListeners.forEach(({ event, wrapped }) => {
      crossTabBroadcast.off(event, wrapped);
    });
  };
};
